import { useTranslation } from "react-i18next"
import { useFragment } from "react-relay"
import { ArrowSquareOut, PencilSimple, TestTube } from "@phosphor-icons/react"
import { ListItem, ListText } from "@/components/ui/list"
import { Tag } from "@/components/ui/tag"
import { joinStrings } from "@/lib/joinString"
import { cn } from "@/lib/utils"
import { ChMeasLaboratoryPrescriptionDrawer } from "@/modules/patients/detail/meas-laboratory-prescription/ch/ch-meas-laboratory-prescription-drawer"
import { chMeasLaboratoryPrescriptionFragment$key } from "@/modules/patients/detail/meas-laboratory-prescription/ch/services/__generated__/chMeasLaboratoryPrescriptionFragment.graphql"
import { chMeasLaboratoryPrescriptionFragment } from "@/modules/patients/detail/meas-laboratory-prescription/ch/services/chMeasLaboratoryPrescriptionFragment"
import { DocumentProps } from "@/modules/patients/detail/progress-note/types/document"
import { useConcludeDialogStoreContext } from "@/modules/patients/detail/store/useConcludeDialogStore"
import { chProgressNoteFragment$data } from "@/modules/patients/services/ch/__generated__/chProgressNoteFragment.graphql"
import { useDrawerStore } from "@/stores/useDrawerStore"

interface MeasLaboratoryPrescriptionDocumentProps extends DocumentProps {
  id?: string
  progressNoteId: string
  measLaboratoryPrescriptionRef: chMeasLaboratoryPrescriptionFragment$key
  fileUploadRequest?: NonNullable<chProgressNoteFragment$data["followUpAppointment"]>["fileUploadRequest"]
  medgateConnectOrder?: NonNullable<chProgressNoteFragment$data["followUpAppointment"]>["medgateConnectOrder"]
}

const MeasLaboratoryPrescriptionDocument = ({
  id,
  progressNoteId,
  isEditable = true,
  measLaboratoryPrescriptionRef,
  isConclusion,
  isSigned,
  isSent,
  fileUploadRequest,
  medgateConnectOrder,
  onSelect,
}: MeasLaboratoryPrescriptionDocumentProps) => {
  const { t } = useTranslation()
  const { bloodChemistries, hematologies, coagulations, urineDiagnostics, rapidTests } =
    useFragment(chMeasLaboratoryPrescriptionFragment, measLaboratoryPrescriptionRef) ?? {}

  const selectedDocumentId = useConcludeDialogStoreContext((state) => state.selectedDocument?.id)
  const triggeredValidation = useConcludeDialogStoreContext((state) => state.triggeredValidation)
  const openDrawer = useDrawerStore((state) => state.openDrawer)
  const isSelected = isConclusion && selectedDocumentId === id

  const bloodChemistriesInfo = bloodChemistries.map((item) =>
    t(`patient.measLaboratoryPrescription.bloodChemistries.${item}`),
  )

  const hematologiesInfo = hematologies.map((item) => t(`patient.measLaboratoryPrescription.hematologies.${item}`))

  const coagulationsInfo = coagulations.map((item) => t(`patient.measLaboratoryPrescription.coagulations.${item}`))

  const urineDiagnosticsInfo = urineDiagnostics.map((item) =>
    t(`patient.measLaboratoryPrescription.urineDiagnostics.${item}`),
  )

  const rapidTestsInfo = rapidTests.map((item) => t(`patient.measLaboratoryPrescription.rapidTests.${item}`))

  return (
    <ListItem
      className={cn(
        "group hover:bg-surface-hovered flex h-12 items-center hover:cursor-pointer",
        isSelected && "bg-primary-surface hover:bg-primary-surface-hovered active:bg-primary-surface-pressed",
        !isEditable && !isConclusion && !isSent && "cursor-auto!",
      )}
      data-testid="meas-laboratory-prescription-document-item"
      onClick={() => {
        if (!isEditable && id) {
          onSelect(id)
          return
        }
        openDrawer(
          () => (
            <ChMeasLaboratoryPrescriptionDrawer
              id={id}
              progressNoteId={progressNoteId}
              measLaboratoryPrescriptionRef={measLaboratoryPrescriptionRef}
              fileUploadRequest={fileUploadRequest}
              medgateConnectOrder={medgateConnectOrder}
            />
          ),
          "MeasLaboratoryPrescriptionDrawer",
        )
      }}>
      <TestTube className={cn("shrink-0", isSelected ? "text-on-primary-surface" : "text-on-surface-subdued")} />
      <div className={cn("ml-2 min-w-0", isEditable && "group-hover:mr-4")}>
        <ListText className={cn(isSelected && "text-on-primary-surface")}>
          {t("patient.measLaboratoryPrescription.measLaboratoryPrescription")}
        </ListText>
        <ListText
          data-testid="meas-laboratory-prescription-details"
          variant="secondary"
          className={cn("truncate", isSelected && "text-on-primary-surface")}>
          {joinStrings(
            [
              ...bloodChemistriesInfo,
              ...hematologiesInfo,
              ...coagulationsInfo,
              ...urineDiagnosticsInfo,
              ...rapidTestsInfo,
            ],
            ", ",
          )}
        </ListText>
      </div>
      {isEditable && (
        <div className="ml-auto hidden group-hover:block">
          <div className="hover:bg-surface-hovered p-2 hover:rounded-full hover:p-2">
            <PencilSimple className="text-on-surface-subdued" data-testid="meas-laboratory-prescription-edit-button" />
          </div>
        </div>
      )}
      {!isConclusion && isSent && (
        <div className="ml-auto hidden group-hover:block">
          <div className="hover:bg-surface-hovered p-2 hover:rounded-full hover:p-2">
            <ArrowSquareOut
              className="text-on-surface-subdued"
              data-testid="meas-laboratory-prescription-document-button"
            />
          </div>
        </div>
      )}
      {isConclusion && (triggeredValidation || isSigned) && (
        <Tag
          className="ml-auto group-hover:hidden"
          type={isSigned ? "success" : "critical"}
          label={isSigned ? t("patient.conclude.signed") : t("patient.conclude.signThis")}
        />
      )}
    </ListItem>
  )
}

export { MeasLaboratoryPrescriptionDocument }
